import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { signInGuard } from 'app/core/auth/guards/sign-in.guard';
import { signOutGuard } from 'app/core/auth/guards/sign-out.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { LockedGuard } from './core/auth/guards/locked.guard';
import { TierGuard } from './core/auth/guards/tier.guard';
import { AdminGlobalGuard } from './core/auth/guards/admin-global.guard';
import { AdminAccessGuard } from './core/auth/guards/admin-access.guard';
import { bootstrapIdentityGuard } from './core/auth/guards/bootstrap-identity.guard';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
	// Redirect empty path to '/dashboards/project'
	{ path: '', pathMatch: 'full', redirectTo: 'dashboard' },

	// Redirect signed-in user to the '/dashboards/project'
	//
	// After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
	// path. Below is another redirection for that path to redirect the user to the desired
	// location. This is a small convenience to keep all main routes together here on this file.
	{ path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard' },

	// Global auth utility route
	// Keep sign-out outside AuthGuard so logout always runs even if auth state is transient.
	{
		path: 'sign-out',
		canActivate: [signOutGuard],
		component: LayoutComponent,
		data: {
			layout: 'empty',
		},
	},

	// Auth routes for guests
	// Auth0 Universal Login handles all UI — these routes just trigger redirects.
	// Legacy routes — redirect to Auth0 login via /sign-in
	{ path: 'reset-password', redirectTo: '/sign-in' },
	{ path: 'activate-account', redirectTo: '/sign-in' },
	// Auth0 login redirect — handled entirely by signInGuard
	{
		path: 'sign-in',
		canActivate: [NoAuthGuard, signInGuard],
		component: LayoutComponent,
		data: {
			layout: 'empty',
		},
	},

	// Internal bootstrap route for environments where users may be missing local records.
	{
		path: 'bootstrap-identity',
		canActivate: [AuthGuard, bootstrapIdentityGuard],
		component: LayoutComponent,
		data: {
			layout: 'empty',
		},
		children: [
			{
				path: '',
				loadComponent: () =>
					import(
						'app/modules/pages/bootstrap-identity/bootstrap-identity.component'
					).then((m) => m.BootstrapIdentityComponent),
			},
		],
	},

	// Landing routes
	{
		path: '',
		component: LayoutComponent,
		data: {
			layout: 'empty',
		},
		children: [],
	},

	// Admin routes
	{
		path: '',
		canActivate: [AuthGuard, AdminAccessGuard],
		canActivateChild: [AuthGuard],
		component: LayoutComponent,
		data: {
			layout: 'empty',
		},
		children: [
			{
				path: 'admin',
				loadChildren: () => import('app/admin/admin.routes'),
			},
		],
	},

	// Claim Mosaic routes
	{
		path: '',
		canActivate: [AuthGuard],
		canActivateChild: [AuthGuard, LockedGuard, TierGuard],
		component: LayoutComponent,
		resolve: {
			initialData: initialDataResolver,
		},
		children: [
			{
				path: 'dashboard',
				loadChildren: () =>
					import('app/modules/dashboard/dashboard.routes'),
			},

			{
				path: 'launchpad',
				loadChildren: () =>
					import('app/modules/launchpad/launchpad.routes'),
			},

			{
				path: 'profile',
				loadChildren: () =>
					import('app/modules/pages/profile/profile.routes'),
			},

			// Claims
			{
				path: 'claims',
				loadChildren: () =>
					import('app/modules/pages/claims/claim.routes'),
			},

			// Companies
			{
				path: 'companies',
				loadChildren: () =>
					import('app/modules/pages/companies/company.routes'),
			},

			// Contacts
			{
				path: 'contacts',
				loadChildren: () =>
					import('app/modules/contacts/contacts.routes'),
			},

			// Mosaic View
			{
				path: 'mosaic-view',
				loadChildren: () =>
					import('app/modules/mosaic-view/mosaic-view.routes'),
			},

			// Leads
			{
				path: 'leads',
				loadChildren: () => import('app/modules/leads/leads.routes'),
			},

			// Tasks
			{
				path: 'tasks',
				loadChildren: () =>
					import('app/modules/pages/tasks/tasks.routes'),
			},

			// Reports
			{
				path: 'reports',
				loadChildren: () =>
					import('app/modules/pages/reports/report.routes'),
			},

			// Settings
			{
				path: 'settings',
				loadChildren: () =>
					import('app/modules/settings/settings.routes'),
			},

				// Agent
				{
					path: 'agent',
					canActivate: [AdminGlobalGuard],
					loadChildren: () => import('app/modules/agent/agent.routes'),
				},

			// Pages
			{
				path: 'pages',
				children: [
					// Error
					{
						path: 'error',
						children: [
							{
								path: '401',
								loadChildren: () =>
									import(
										'app/modules/pages/error/error-401/error-401.routes'
									),
							},
							{
								path: '404',
								loadChildren: () =>
									import(
										'app/modules/pages/error/error-404/error-404.routes'
									),
							},
							{
								path: '500',
								loadChildren: () =>
									import(
										'app/modules/pages/error/error-500/error-500.routes'
									),
							},
						],
					},

					// Loading
					{
						path: 'loading',
						loadChildren: () =>
							import('app/modules/pages/loading/loading.routes'),
					},
				],
			},

			// 404 & Catch all
			{
				path: '404-not-found',
				pathMatch: 'full',
				loadChildren: () =>
					import(
						'app/modules/pages/error/error-404/error-404.routes'
					),
			},
			{ path: '**', redirectTo: '404-not-found' },
		],
	},
];
